/**
 * Smooth Sales cart upsells ("in-cart specials").
 *
 * Fetches Shopify product recommendations seeded by the newest cart item and
 * renders compact one-tap add rows inside the cart drawer / cart page.
 *
 * - Uses the recommendations intent from theme settings (complementary pairs
 *   best with the free Search & Discovery app; falls back to related when the
 *   complementary set is empty).
 * - Excludes products already in the cart.
 * - Single-variant products add straight to the cart and announce the change
 *   with Shopify's standard CartLinesUpdateEvent (with rendered sections), so
 *   Horizon's drawer, icon, and totals all update in one round trip.
 * - Multi-variant products link to the product page instead of guessing a
 *   variant.
 *
 * The element's data attributes are re-rendered by the Section Rendering API
 * after every cart mutation; `attributeChangedCallback` keys the refresh.
 */

import { CartLinesUpdateEvent } from '@shopify/events';

const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

class CartUpsells extends HTMLElement {
  static observedAttributes = ['data-cart-product-ids', 'data-seed-product-id'];

  #abortController = null;

  connectedCallback() {
    this.#refresh();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue !== null && oldValue !== newValue && this.isConnected) this.#refresh();
  }

  get #cartProductIds() {
    return new Set(
      (this.dataset.cartProductIds || '')
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean)
    );
  }

  async #refresh() {
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    const limit = Number(this.dataset.limit) || 4;
    const intent = this.dataset.intent || 'complementary';
    const seed = this.dataset.seedProductId;

    try {
      let products = [];
      if (this.dataset.source === 'collection' && this.dataset.collection) {
        products = await this.#fetchCollection(this.dataset.collection, limit, signal);
      } else if (this.dataset.cartCollection) {
        // "Same aisle" first: products from the newest cart item's own
        // collection, then the recommendations chain below.
        products = await this.#fetchCollection(this.dataset.cartCollection, limit, signal);
      }
      if (products.length === 0 && seed) {
        products = await this.#fetchRecommendations(seed, intent, limit, signal);
        if (products.length === 0 && intent !== 'related') {
          products = await this.#fetchRecommendations(seed, 'related', limit, signal);
        }
      }
      if (products.length === 0 && this.dataset.source !== 'collection' && this.dataset.collection) {
        // Last resort: the curated collection, so the row never goes empty.
        products = await this.#fetchCollection(this.dataset.collection, limit, signal);
      }
      if (signal.aborted) return;
      this.#render(products.slice(0, limit));
    } catch (error) {
      if (error?.name !== 'AbortError') this.replaceChildren();
    }
  }

  /* The curated-collection source. /collections/<handle>/products.json has a
     different shape than the recommendations API (prices are dollar strings,
     no top-level availability), so normalise before rendering. */
  async #fetchCollection(handle, limit, signal) {
    const url = `${root}collections/${encodeURIComponent(handle)}/products.json?limit=${limit * 3}`;
    const response = await fetch(url, { signal, headers: { Accept: 'application/json' } });
    if (!response.ok) return [];
    const data = await response.json();
    const inCart = this.#cartProductIds;
    return (data.products || [])
      .map((product) => this.#normalizeCollectionProduct(product))
      .filter((product) => product.available && !inCart.has(String(product.id)));
  }

  #normalizeCollectionProduct(product) {
    const variants = (product.variants || []).map((variant) => ({
      id: variant.id,
      available: variant.available !== false,
    }));
    const priceString = product.variants?.[0]?.price || '0';
    return {
      id: product.id,
      title: product.title,
      url: `${root}products/${product.handle}`,
      available: variants.some((variant) => variant.available),
      featured_image: product.images?.[0]?.src || null,
      price: Math.round(parseFloat(priceString) * 100),
      variants,
    };
  }

  async #fetchRecommendations(seed, intent, limit, signal) {
    const url = `${root}recommendations/products.json?product_id=${encodeURIComponent(seed)}&limit=${limit * 2}&intent=${encodeURIComponent(intent)}`;
    const response = await fetch(url, { signal, headers: { Accept: 'application/json' } });
    if (!response.ok) return [];
    const data = await response.json();
    const inCart = this.#cartProductIds;
    return (data.products || []).filter(
      (product) => product.available && !inCart.has(String(product.id))
    );
  }

  #render(products) {
    if (products.length === 0) {
      this.replaceChildren();
      return;
    }

    const heading = document.createElement('p');
    heading.className = 'cart-upsells__heading';
    heading.textContent = this.dataset.heading || 'You Might Also Like';

    const list = document.createElement('ul');
    list.className = 'cart-upsells__list';
    list.setAttribute('role', 'list');

    for (const product of products) {
      list.appendChild(this.#renderItem(product));
    }

    this.replaceChildren(heading, list);
  }

  #renderItem(product) {
    const item = document.createElement('li');
    item.className = 'cart-upsells__item';

    if (product.featured_image) {
      const image = document.createElement('img');
      image.className = 'cart-upsells__image';
      image.src = this.#resizeImage(product.featured_image, 120);
      image.alt = '';
      image.loading = 'lazy';
      image.width = 52;
      image.height = 52;
      item.appendChild(image);
    }

    const info = document.createElement('div');
    info.className = 'cart-upsells__info';

    const title = document.createElement('a');
    title.className = 'cart-upsells__title';
    title.href = product.url;
    title.textContent = product.title;

    const price = document.createElement('span');
    price.className = 'cart-upsells__price';
    price.textContent = this.#formatPrice(product.price);

    info.append(title, price);
    item.appendChild(info);

    const singleVariant = product.variants && product.variants.length === 1;
    if (singleVariant) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'cart-upsells__add';
      button.textContent = 'Add';
      button.addEventListener('click', () => this.#addToCart(button, product.variants[0].id));
      item.appendChild(button);
    } else {
      const link = document.createElement('a');
      link.className = 'cart-upsells__add';
      link.href = product.url;
      link.textContent = 'Choose';
      item.appendChild(link);
    }

    return item;
  }

  async #addToCart(button, variantId) {
    button.disabled = true;

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart-upsells',
        lines: [{ merchandiseId: String(variantId), quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const formData = new FormData();
      formData.append('id', String(variantId));
      formData.append('quantity', '1');
      // Hidden line property (underscore = never shown to the customer;
      // theme cart and Shopify checkout both filter it) so the ORDER in
      // admin records that this line came from the cart upsell row.
      formData.append('properties[_added_from]', 'You Might Also Like (cart suggestions)');
      const sectionIds = [...document.querySelectorAll('cart-items-component[data-section-id]')]
        .map((component) => component.dataset.sectionId)
        .filter(Boolean);
      if (sectionIds.length > 0) formData.append('sections', [...new Set(sectionIds)].join(','));

      const addUrl = typeof Theme !== 'undefined' && Theme.routes?.cart_add_url ? Theme.routes.cart_add_url : `${root}cart/add.js`;
      const addResponse = await fetch(addUrl, {
        method: 'POST',
        body: formData,
        headers: { Accept: 'application/json' },
      });
      const addResult = await addResponse.json();
      if (addResult.status) throw new Error(addResult.message || 'Add to cart failed');

      const cart = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          sections: addResult.sections || undefined,
          source: 'cart-upsells',
          itemCount: 1,
          productId: String(addResult.product_id || ''),
        },
      });
    } catch (error) {
      deferred.reject(error);
      button.disabled = false;
    }
  }

  #resizeImage(url, size) {
    try {
      const parsed = new URL(url, window.location.origin);
      parsed.searchParams.set('width', String(size));
      return parsed.toString();
    } catch (error) {
      return url;
    }
  }

  #formatPrice(cents) {
    if (typeof cents !== 'number') return '';
    return `$${(cents / 100).toFixed(2)}`;
  }
}

if (!customElements.get('cart-upsells')) customElements.define('cart-upsells', CartUpsells);
